import {
  ChevronRight,
  Copy,
  ExternalLink,
  FileText,
  Loader2,
} from "lucide-react";
import { useState } from "react";
import { useGetList, useLocaleState, useNotify, useTranslate } from "ra-core";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Project, ProjectContactLink } from "../types";
import { ConnectionSentPrompt } from "./LinkedInReminder";
import { formatDay } from "./projectUtils";
import { SellerReferencesNotice } from "./SellerReferences";
import { LockNote } from "./Teaser";
import { useInvited } from "./useInvited";

// Fait de la fiche cité par l'assistant, avec sa source (renvoyé par la fonction project-assistant).
type Signal = {
  id: string;
  label: string;
  inferred: boolean;
  url: string | null;
  date: string | null;
  origin?: "project" | "person" | "company" | "reference";
};

type Brief = {
  summary: string;
  why_relevant: string;
  who_first: string;
  signals: Signal[];
};

type Draft = {
  format: "email" | "linkedin";
  subject: string;
  body: string;
  // LinkedIn : note de la demande de connexion (300 caractères au plus chez LinkedIn).
  connection_note: string;
  // Observation d'ouverture choisie par le modèle, ou aucune s'il n'en a trouvé aucune de sûre.
  hook: { text: string; origin: NonNullable<Signal["origin"]> } | null;
  signals: Signal[];
};

// Limite de LinkedIn pour la note d'une demande de connexion.
const CONNECTION_NOTE_MAX = 300;

const call = async <T,>(body: object): Promise<T> => {
  const { data, error } = await getSupabaseClient().functions.invoke<T>(
    "project-assistant",
    { method: "POST", body },
  );
  if (error) {
    // Le message de la fonction (plafond atteint, contact opposé…) est plus utile que le statut HTTP.
    const message =
      error instanceof FunctionsHttpError
        ? (await error.context.json().catch(() => null))?.message
        : null;
    throw Object.assign(new Error(message ?? error.message), {
      status:
        error instanceof FunctionsHttpError ? error.context.status : undefined,
    });
  }
  return data as T;
};

/** Assistant de la fiche (spec § 10) : synthèse et premier message, à relire et envoyer soi-même. */
export const ProjectAssistant = ({ project }: { project: Project }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale] = useLocaleState();
  const [brief, setBrief] = useState<Brief | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState<"brief" | "draft" | null>(null);
  const [recipient, setRecipient] = useState<string>("");
  const [format, setFormat] = useState<"email" | "linkedin">("email");
  const [language, setLanguage] = useState<string>("auto");
  // Invité : 5 analyses ou messages sur 7 jours (la fonction le vérifie) ; au-delà, le motif du cadenas.
  const invited = useInvited();
  const [limited, setLimited] = useState(false);
  // Personne du brouillon LinkedIn, et note copiée : la barre demande alors si la demande est partie.
  const [draftContact, setDraftContact] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [noteCopied, setNoteCopied] = useState(false);
  // Fermé par défaut, pour raccourcir la fiche : s'ouvre au clic sur le titre ou avec l'analyse.
  const [open, setOpen] = useState(false);
  const { data: contacts } = useGetList<ProjectContactLink>(
    "project_contact_links",
    {
      filter: { project_id: project.id },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "created_at", order: "ASC" },
    },
  );

  const run = async (kind: "brief" | "draft") => {
    setOpen(true);
    setBusy(kind);
    try {
      if (kind === "brief") {
        setBrief(
          await call<Brief>({
            project_id: project.id,
            action: "brief",
            language: locale,
          }),
        );
      } else {
        const person = contacts?.find(
          (contact) =>
            String(contact.contact_id) === recipient &&
            contact.kind === "person",
        );
        setNoteCopied(false);
        setDraftContact(
          person
            ? {
                id: Number(person.contact_id),
                name: [person.first_name, person.last_name]
                  .filter(Boolean)
                  .join(" "),
              }
            : null,
        );
        setDraft(
          await call<Draft>({
            project_id: project.id,
            action: "message",
            contact_id: Number(recipient),
            format,
            language: language === "auto" ? undefined : language,
          }),
        );
      }
    } catch (error) {
      if (invited && (error as { status?: number }).status === 429)
        setLimited(true);
      else notify((error as Error).message, { type: "error" });
    } finally {
      setBusy(null);
    }
  };

  // Chaque bloc se copie à part, pour être collé tel quel dans son champ (objet, message, note).
  const copy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    notify("resources.projects.assistant.copied", { type: "info" });
  };

  const label = (contact: ProjectContactLink) =>
    contact.kind === "person"
      ? `${[contact.first_name, contact.last_name].filter(Boolean).join(" ")}${contact.title ? `, ${contact.title}` : ""} (${contact.company_name})`
      : `${translate("resources.projects.contacts.kinds.service")} (${contact.company_name})`;

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap justify-between items-center gap-2">
          <h3 className="text-sm font-bold">
            <button
              type="button"
              aria-expanded={open}
              className="flex items-center gap-1 rounded-[3px]"
              onClick={() => setOpen(!open)}
            >
              <ChevronRight
                className={`size-4 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
                aria-hidden
              />
              {translate("resources.projects.assistant.title")}
            </button>
          </h3>
          <Button
            size="sm"
            variant="outline"
            disabled={busy !== null}
            onClick={() => run("brief")}
          >
            {busy === "brief" ? (
              <Loader2 className="animate-spin" />
            ) : (
              <FileText />
            )}
            {translate("resources.projects.assistant.brief")}
          </Button>
        </div>
        {open && (
          <>
            <p className="text-xs text-muted-foreground">
              {translate("resources.projects.assistant.intro")}
              {invited &&
                ` ${translate("resources.projects.teaser.assistant_quota")}`}
            </p>
            {limited && (
              <LockNote className="text-xs">
                {translate("resources.projects.teaser.assistant_limit")}
              </LockNote>
            )}

            {brief && (
              <div className="flex flex-col gap-3 text-sm">
                {(["summary", "why_relevant", "who_first"] as const).map(
                  (key) => (
                    <div key={key}>
                      <div className="text-xs font-medium text-muted-foreground">
                        {translate(`resources.projects.assistant.${key}`)}
                      </div>
                      <p className="whitespace-pre-line">{brief[key]}</p>
                    </div>
                  ),
                )}
                <Signals signals={brief.signals} locale={locale} />
              </div>
            )}

            <div className="flex flex-col gap-3 border-t pt-4">
              <h4 className="text-sm font-medium">
                {translate("resources.projects.assistant.draft_title")}
              </h4>
              {!contacts?.length ? (
                <p className="text-sm text-muted-foreground">
                  {translate("resources.projects.assistant.no_recipient")}
                </p>
              ) : (
                <>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Select value={recipient} onValueChange={setRecipient}>
                      <SelectTrigger className="sm:flex-1 min-w-0">
                        <SelectValue
                          placeholder={translate(
                            "resources.projects.assistant.recipient",
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {contacts.map((contact) => (
                          <SelectItem
                            key={contact.id}
                            value={String(contact.contact_id)}
                          >
                            {label(contact)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={language} onValueChange={setLanguage}>
                      <SelectTrigger
                        className="sm:w-56"
                        aria-label={translate(
                          "resources.projects.assistant.language",
                        )}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["auto", "fr", "en"].map((code) => (
                          <SelectItem key={code} value={code}>
                            {translate(
                              `resources.projects.assistant.languages.${code}`,
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <SellerReferencesNotice />
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <ToggleGroup
                      type="single"
                      variant="outline"
                      size="sm"
                      value={format}
                      onValueChange={(value) =>
                        value && setFormat(value as "email" | "linkedin")
                      }
                    >
                      {(["email", "linkedin"] as const).map((value) => (
                        <ToggleGroupItem key={value} value={value}>
                          {translate(
                            `resources.projects.assistant.formats.${value}`,
                          )}
                        </ToggleGroupItem>
                      ))}
                    </ToggleGroup>
                    <Button
                      size="sm"
                      disabled={!recipient || busy !== null}
                      onClick={() => run("draft")}
                    >
                      {busy === "draft" && <Loader2 className="animate-spin" />}
                      {translate(
                        `resources.projects.assistant.${busy === "draft" ? "working" : "draft"}`,
                      )}
                    </Button>
                  </div>
                </>
              )}

              {draft && (
                <div className="flex flex-col gap-3">
                  <p className="text-xs text-muted-foreground">
                    {draft.hook
                      ? translate("resources.projects.assistant.hook", {
                          hook: draft.hook.text,
                          origin: translate(
                            `resources.projects.assistant.hook_origins.${draft.hook.origin}`,
                          ),
                        })
                      : translate("resources.projects.assistant.no_hook")}
                  </p>
                  {draft.format === "email" && (
                    <CopyBlock
                      label={translate("resources.projects.assistant.subject")}
                      onCopy={() => copy(draft.subject)}
                    >
                      <Input
                        aria-label={translate(
                          "resources.projects.assistant.subject",
                        )}
                        value={draft.subject}
                        onChange={(e) =>
                          setDraft({ ...draft, subject: e.target.value })
                        }
                      />
                    </CopyBlock>
                  )}
                  {draft.format === "linkedin" && (
                    <CopyBlock
                      label={translate(
                        "resources.projects.assistant.connection_note",
                      )}
                      hint={translate(
                        "resources.projects.assistant.characters",
                        {
                          count: draft.connection_note.length,
                          max: CONNECTION_NOTE_MAX,
                        },
                      )}
                      warn={draft.connection_note.length > CONNECTION_NOTE_MAX}
                      onCopy={async () => {
                        await copy(draft.connection_note);
                        setNoteCopied(true);
                      }}
                    >
                      <Textarea
                        aria-label={translate(
                          "resources.projects.assistant.connection_note",
                        )}
                        className="min-h-24 text-sm"
                        value={draft.connection_note}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            connection_note: e.target.value,
                          })
                        }
                      />
                    </CopyBlock>
                  )}
                  {draft.format === "linkedin" &&
                    noteCopied &&
                    draftContact &&
                    !invited && (
                      <ConnectionSentPrompt
                        project={project}
                        contact={draftContact}
                        message={draft.body}
                        onClose={() => setNoteCopied(false)}
                      />
                    )}
                  <CopyBlock
                    label={translate(
                      `resources.projects.assistant.${draft.format === "linkedin" ? "linkedin_body" : "body"}`,
                    )}
                    onCopy={() => copy(draft.body)}
                  >
                    <Textarea
                      aria-label={translate(
                        "resources.projects.assistant.body",
                      )}
                      className="min-h-56 text-sm"
                      value={draft.body}
                      onChange={(e) =>
                        setDraft({ ...draft, body: e.target.value })
                      }
                    />
                  </CopyBlock>
                  {draft.format === "linkedin" && (
                    <span className="text-xs text-muted-foreground">
                      {translate("resources.projects.assistant.linkedin_help")}
                    </span>
                  )}
                  <Signals signals={draft.signals} locale={locale} />
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

/** Bloc à copier tel quel : son titre, son bouton « Copier » et, au besoin, un compteur. */
const CopyBlock = ({
  label,
  hint,
  warn = false,
  onCopy,
  children,
}: {
  label: string;
  hint?: string;
  warn?: boolean;
  onCopy: () => void;
  children: React.ReactNode;
}) => {
  const translate = useTranslate();
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">
          {label}
          {hint && (
            <span className={warn ? " text-destructive" : ""}> · {hint}</span>
          )}
        </span>
        <Button size="sm" variant="ghost" onClick={onCopy}>
          <Copy />
          {translate("resources.projects.assistant.copy")}
        </Button>
      </div>
      {children}
    </div>
  );
};

/** Faits de la fiche sur lesquels s'appuie le texte, chacun avec sa source. */
const Signals = ({
  signals,
  locale,
}: {
  signals: Signal[];
  locale: string;
}) => {
  const translate = useTranslate();
  if (!signals.length) return null;
  return (
    <details className="text-xs text-muted-foreground">
      <summary className="cursor-pointer">
        {translate("resources.projects.assistant.signals")} ({signals.length})
      </summary>
      <ul className="mt-2 flex flex-col gap-1">
        {signals.map((signal) => (
          <li key={signal.id}>
            {signal.label}
            {signal.inferred &&
              ` · ${translate("resources.projects.assistant.inferred")}`}
            {" · "}
            {signal.url ? (
              <a
                href={signal.url}
                target="_blank"
                rel="noopener noreferrer"
                className="underline inline-flex items-center gap-0.5"
              >
                {translate("resources.projects.assistant.source", {
                  date: formatDay(signal.date, locale),
                })}
                <ExternalLink className="h-3 w-3" />
              </a>
            ) : (
              translate(
                `resources.projects.assistant.${signal.inferred ? "assessment" : "from_base"}`,
              )
            )}
          </li>
        ))}
      </ul>
    </details>
  );
};
